import React, { useContext, useMemo } from 'react'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/atoms/modal'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import { css } from '@emotion/core'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { SpaceReservationExportInput } from '@/models/space-manager'
import dayjs from 'dayjs'
import { exportReservationSpaces } from '@/apis/aipass'
import { errorHandler } from '@/libs/errors'
import { ReservationNoteCategory } from '@/models/reservation/reservation-note'
import { LoaderContextCreator } from '@/contexts/loader'
import { ButtonType } from '@/components/atoms/button'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { Checkbox } from '@/components/atoms/checkbox'
import { SpaceAsset } from '@/models/guest-app/asset'

interface Props {
  onClose: () => void
  spaceAssets: SpaceAsset[]
  date: dayjs.Dayjs
}
export const SpaceReservationExportModal: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const useFormReturn = useForm<SpaceReservationExportInput>({
    defaultValues: {
      day: props.date.format('YYYY-MM-DD'),
      noteCategory: ReservationNoteCategory.Front,
      filterAssetId: props.spaceAssets.map(asset => ({ id: asset.id, value: true, title: asset.title })),
    },
  })

  const { control, handleSubmit } = useFormReturn
  const watchFilterAssetId = useWatch({ control, name: 'filterAssetId' })
  const filterAssetField = useFieldArray({
    control,
    name: 'filterAssetId',
  })

  const downloadReport = async (formValue: SpaceReservationExportInput) => {
    try {
      setIsLoading(true)
      const request = {
        ...formValue,
        filterAssetId: formValue.filterAssetId.filter(field => field.value).map(field => field.id),
      }
      const { url } = await exportReservationSpaces(request)
      window.open(url, '_blank')
    } catch (e) {
      errorHandler(e)
    } finally {
      setIsLoading(false)
    }
  }

  const isValid: boolean = useMemo(() => watchFilterAssetId.find(field => field.value) !== undefined, [watchFilterAssetId])

  return (
    <>
      <Modal customCss={modalStyle}>
        <ModalHeader>{t('Report output')}</ModalHeader>
        <ModalBody>
          <div className="container">
            <div className="block">
              <div className="input-label">{t('Output reservation note')}</div>
              <Controller
                name="noteCategory"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <RadioGroupField
                    value={value}
                    groupCss={{ gap: 24 }}
                    style={{ radioSize: 21 }}
                    items={[
                      { value: ReservationNoteCategory.Front, label: t('reservationNote.front') },
                      { value: ReservationNoteCategory.Cleaning, label: t('reservationNote.cleaning') },
                      { value: ReservationNoteCategory.Meal, label: t('reservationNote.meal') },
                    ]}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <div className="block">
              <div className="input-label">{t('Output assets')}</div>

              <div className="asset-check-block">
                {filterAssetField.fields.map((item, index) => (
                  <Controller
                    key={item.id}
                    name={`filterAssetId.${index}.value`}
                    control={control}
                    render={({ field: { value, onChange } }) => <Checkbox value={value} label={item.title} onChange={onChange} />}
                  />
                ))}
                {!isValid && <div className="error-text">{t('Please select {{item}}', { item: t('Output assets') })}</div>}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button buttonType={ButtonType.Secondary} height="38px" width="110px" marginRight={16} onClick={props.onClose}>
            {t('Cancel')}
          </Button>
          <Button
            buttonType={isValid ? ButtonType.Primary : ButtonType.PrimaryDisabled}
            type="submit"
            height="38px"
            width="110px"
            isDisabled={!isValid}
            onClick={handleSubmit(downloadReport)}
          >
            {t('Download')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

const modalStyle = css({
  top: '5%',
  height: 'auto',
  minHeight: 'auto',
  maxHeight: 'calc(100vh - 10%)',
  width: 580,
  '.container': {
    padding: '24px 32px',
    border: '1px solid #ccc',
    borderRadius: 5,
    background: '#fff',
  },
  'div.block': {
    paddingTop: 24,
    ':first-of-type': { paddingTop: 0 },
    '.input-label': {
      fontSize: 12,
      fontWeight: 'bold',
      letterSpacing: 0.6,
      color: '#676767',
      paddingBottom: 12,
    },
    '.asset-check-block': {
      display: 'flex',
      gap: 8,
      flexDirection: 'column',
      position: 'relative',
      '.error-text': {
        fontSize: 10,
        color: 'rgba(255, 0, 0, 0.5)',
        bottom: 'calc(-1em - 4px)',
        position: 'absolute',
      },
    },
  },
})
